import { useRef } from 'react'
import { useAppSelector } from '@services/store'
import {
	REQUEST_CONFIGS_KEYS,
	REQUEST_TN_DETAILS_STEPS_NAME
} from '@services/constants'
import requestForm from '../../request.form'
import * as utilsList from '../__index.utils'
import { RadioList } from '@utils/request'
import { isRenovationSectionB } from '../__index.utils'

export const STEP5FIELDS = {
	isWorkDueToDisaster: 'isWorkDueToDisaster',
	workDueToDisasterPrecision: 'workDueToDisasterPrecision',
	approximateCostOfWork: 'approximateCostOfWork',
	isTreeCuttingRequired: 'isTreeCuttingRequired',
	additionalInfoQ28: 'additionalInfoQ28',
	workStartDate: 'workStartDate',
	workEndDate: 'workEndDate',
	whoWillBeInChargeOfWork: 'whoWillBeInChargeOfWork',
	entrepreneurUnknown1: 'entrepreneurUnknown1',
	companyNameExecutingWork: 'companyNameExecutingWork',
	companyNEQNumber: 'companyNEQNumber',
	companyRBQNumber: 'companyRBQNumber',
	companyRepresentativeName: 'companyRepresentativeName',
	companyRepresentativePhoneNumber: 'companyRepresentativePhoneNumber',
	companyRepresentativeEmail: 'companyRepresentativeEmail',
	companyAddress: 'companyAddress',
	companyCity: 'companyCity',
	companyPostalCode: 'companyPostalCode'
} as const

export type Step5Fields = keyof typeof STEP5FIELDS

/**
 * useStep5State - Custom hook to manage the state for step 5 of the GR7 commercial Permit form..
 * It retrieves the necessary answers from the Redux store and prepares the state for the form inputs.
 *
 * @returns {Object} An object containing the state for each input in step 5.
 */

export function useStep5State() {
	const answers = useAppSelector((state) => state.request.configs)
	const { pageAssets } = requestForm()

	const radioListWhoWillBeInChargeOfWork: RadioList[] =
		utilsList.radioListWhoWillBeInChargeOfWork(pageAssets)
	const radioListYesNo: RadioList[] = utilsList.radioListYesNo(pageAssets)

	//-------------------- Informations sur les travaux INPUTS ------------------------

	// -------------------  DETAILS_5 -------------------

	return {
		// Effectuez-vous ces travaux à la suite d'une catastrophe?
		isWorkDueToDisaster: {
			name: 'isWorkDueToDisaster',
			label:
				pageAssets?.request_form_buildingConstructionOrAdditionPermit_isWorkDueToDisaster,
			labelKey:
				'request_form_buildingConstructionOrAdditionPermit_isWorkDueToDisaster',
			value: answers?.isWorkDueToDisaster?.toString() || '',
			required: true,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_5
		},
		// Veuillez préciser WorkDueToDisasterPrecision
		workDueToDisasterPrecision: {
			name: 'workDueToDisasterPrecision',
			label:
				pageAssets?.request_form_buildingConstructionOrAdditionPermit_workDueToDisasterPrecision,
			labelKey:
				'request_form_buildingConstructionOrAdditionPermit_workDueToDisasterPrecision',
			value: answers?.workDueToDisasterPrecision || '',
			required: answers?.isWorkDueToDisaster?.toString() == 'true',
			ref: useRef<HTMLTextAreaElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_5
		},
		// Coût approximatif des travaux
		approximateCostOfWork: {
			name: 'approximateCostOfWork',
			label:
				pageAssets?.request_form_buildingConstructionOrAdditionPermit_approximateCostOfWork,
			labelKey:
				'request_form_buildingConstructionOrAdditionPermit_approximateCostOfWork',
			value: answers?.approximateCostOfWork || '',
			required: true,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_5
		},
		// Est-ce qu'un abattage d'arbres est requis?
		isTreeCuttingRequired: {
			name: 'isTreeCuttingRequired',
			label:
				pageAssets?.request_form_buildingConstructionOrAdditionPermit_isTreeCuttingRequired,
			labelKey:
				'request_form_buildingConstructionOrAdditionPermit_isTreeCuttingRequired',
			value: answers?.isTreeCuttingRequired?.toString() || '',
			required: isRenovationSectionB(answers.typesOfWorkRenovation),
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_5
		},
		// Renseignements supplémentaires
		// additionalInfoQ28: {
		// 	name: 'additionalInfoQ28',
		// 	label:
		// 		pageAssets?.request_form_buildingConstructionOrAdditionPermit_additionalInfoQ28,
		// 	labelKey:
		// 		'request_form_buildingConstructionOrAdditionPermit_additionalInfoQ28',
		// 	description:
		// 		pageAssets?.request_form_buildingConstructionOrAdditionPermit_additionalInfoQ28_description,
		// 	value: answers?.additionalInfoQ28?.toString() || '',
		// 	required: false,
		// 	ref: useRef<HTMLTextAreaElement>(null),
		// 	subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_5
		// },

		// Date de début (projetée)
		workStartDate: {
			name: 'workStartDate',
			label:
				pageAssets?.request_form_buildingConstructionOrAdditionPermit_startDate,
			labelKey: 'request_form_buildingConstructionOrAdditionPermit_startDate',
			value: answers?.workStartDate || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_5
		},
		// Date de fin (projetée)
		workEndDate: {
			name: 'workEndDate',
			label:
				pageAssets?.request_form_buildingConstructionOrAdditionPermit_endDate,
			labelKey: 'request_form_buildingConstructionOrAdditionPermit_endDate',
			value: answers?.workEndDate || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_5
		},

		//* Entrepreneur exécutant les travaux de construction.

		// Qui sera en charge des travaux?
		whoWillBeInChargeOfWork: {
			name: 'whoWillBeInChargeOfWork',
			label:
				pageAssets?.request_form_buildingConstructionOrAdditionPermit_whoWillBeInChargeOfWork,
			labelKey:
				'request_form_buildingConstructionOrAdditionPermit_whoWillBeInChargeOfWork',
			value: answers?.whoWillBeInChargeOfWork?.toString() || '',
			required: true,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_5
		},
		// Je ne connais pas encore l'entrepreneur
		entrepreneurUnknown1: {
			name: 'entrepreneurUnknown1',
			label:
				pageAssets?.request_form_buildingConstructionOrAdditionPermit_entrepreneurUnknown,
			labelKey:
				'request_form_buildingConstructionOrAdditionPermit_entrepreneurUnknown',
			value: answers.entrepreneurUnknown1?.toString() || String(false),
			required: true,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_5
		},

		// Nom de l'entreprise exécutant les travaux de construction.
		companyNameExecutingWork: {
			name: 'companyNameExecutingWork',
			label:
				pageAssets?.request_form_buildingConstructionOrAdditionPermit_companyNameExecutingWork,
			labelKey:
				'request_form_buildingConstructionOrAdditionPermit_companyNameExecutingWork',
			value: answers.companyNameExecutingWork || '',
			required:
				answers?.whoWillBeInChargeOfWork ==
					radioListWhoWillBeInChargeOfWork[0].value ||
				answers.entrepreneurUnknown1 == true
					? false
					: true,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_5
		},
		// Numéro d'entreprise du Québec (NEQ)
		companyNEQNumber: {
			name: 'companyNEQNumber',
			label:
				pageAssets?.request_form_buildingConstructionOrAdditionPermit_companyNEQNumber,
			labelKey:
				'request_form_buildingConstructionOrAdditionPermit_companyNEQNumber',
			value: answers.companyNEQNumber || '',
			required:
				answers?.whoWillBeInChargeOfWork ==
					radioListWhoWillBeInChargeOfWork[0].value ||
				answers.entrepreneurUnknown1 == true
					? false
					: true,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_5
		},
		// Numéro de licence RBQ
		companyRBQNumber: {
			name: 'companyRBQNumber',
			label:
				pageAssets?.request_form_buildingConstructionOrAdditionPermit_companyRBQNumber,
			labelKey:
				'request_form_buildingConstructionOrAdditionPermit_companyRBQNumber',
			value: answers.companyRBQNumber || '',
			required:
				answers?.whoWillBeInChargeOfWork ==
					radioListWhoWillBeInChargeOfWork[0].value ||
				answers.entrepreneurUnknown1 == true
					? false
					: true,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_5
		},

		// Nom complet du (de la) représentant(e) ou de la personne contact
		companyRepresentativeName: {
			name: 'companyRepresentativeName',
			label:
				pageAssets?.request_form_buildingConstructionOrAdditionPermit_companyRepresentativeName,
			labelKey:
				'request_form_buildingConstructionOrAdditionPermit_companyRepresentativeName',
			value: answers.companyRepresentativeName || '',
			required:
				answers?.whoWillBeInChargeOfWork ==
					radioListWhoWillBeInChargeOfWork[0].value ||
				answers.entrepreneurUnknown1 == true
					? false
					: true,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_5
		},
		// Téléphone
		companyRepresentativePhoneNumber: {
			name: 'companyRepresentativePhoneNumber',
			label: pageAssets?.label_telephone,
			labelKey: 'label_telephone',
			value: answers.companyRepresentativePhoneNumber || '',
			required:
				answers?.whoWillBeInChargeOfWork ==
					radioListWhoWillBeInChargeOfWork[0].value ||
				answers.entrepreneurUnknown1 == true
					? false
					: true,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_5
		},
		companyRepresentativeEmail: {
			name: 'companyRepresentativeEmail',
			label: pageAssets?.label_email,
			labelKey: 'label_email',
			value: answers.companyRepresentativeEmail || '',
			required:
				answers?.whoWillBeInChargeOfWork ==
					radioListWhoWillBeInChargeOfWork[0].value ||
				answers.entrepreneurUnknown1 == true
					? false
					: true,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_5
		},
		// Adresse de l'entreprise
		companyAddress: {
			name: 'companyAddress',
			label: pageAssets?.label_address,
			labelKey: 'label_address',
			value: answers.companyAddress || '',
			required:
				answers?.whoWillBeInChargeOfWork ==
					radioListWhoWillBeInChargeOfWork[0].value ||
				answers.entrepreneurUnknown1 == true
					? false
					: true,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_5
		},
		companyCity: {
			name: 'companyCity',
			label: pageAssets?.label_city,
			labelKey: 'label_city',
			value: answers.companyCity || '',
			required:
				answers?.whoWillBeInChargeOfWork ==
					radioListWhoWillBeInChargeOfWork[0].value ||
				answers.entrepreneurUnknown1 == true
					? false
					: true,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_5
		},
		companyPostalCode: {
			name: 'companyPostalCode',
			label: pageAssets?.label_postalCode,
			labelKey: 'label_postalCode',
			value: answers.companyPostalCode || '',
			required:
				answers?.whoWillBeInChargeOfWork ==
					radioListWhoWillBeInChargeOfWork[0].value ||
				answers.entrepreneurUnknown1 == true
					? false
					: true,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_5
		}
	}
}
